/* eslint-disable @typescript-eslint/explicit-module-boundary-types */

import { useConfAndQueryStore, useLayoutStore } from '@/store'
import { nextTick, onMounted, ref } from 'vue'
import useSelect from './use-select'
import { ProductCategoryConfig, ProductCategoryValue } from '@/data/const'
import { FORMATTED_PRODUCT_SERIE, PRODUCT_SERIES_LIST } from '@/data/series'
import { delay } from '@/utils'
import { NewWorksItemWithCondition, StackingSet } from '@/data/newworks'

const useNewWorks = () => {
  const confAndQueryStore = useConfAndQueryStore()
  const { gotoAllWorks, showManualLoading } = useLayoutStore()
  const {
    selectableProducts,
    selectableStackings,
    selectCategory,
    selectCategoryAndAttr,
    selectStackingSet,
    selectSeries,
    selectProduct,
    selectStacking,
    reset
  } = useSelect()

  const isSimulated = ref(false)
  const scrollFunc = ref<(index: number, smooth: boolean) => void>()

  const tryOnStacking = async () => {
    await nextTick()
    const stacking = selectableStackings.value.find(
      p => p.code.toUpperCase() === confAndQueryStore.toSelect.item
    )
    if (stacking) {
      selectStacking(stacking)
      await showManualLoading()
      gotoAllWorks(true, false)
    } else {
      reset()
    }
  }

  const tryOnProduct = async () => {
    await nextTick()
    const product = selectableProducts.value.find(
      p => p.skuId.toUpperCase() === confAndQueryStore.toSelect.item
    )
    if (product) {
      selectProduct(product)
      await showManualLoading()
      gotoAllWorks(true, false)
    } else {
      reset()
    }
  }

  const selectSeriesIfIsH6951 = (
    item: StackingSet | NewWorksItemWithCondition
  ) => {
    if (item.key === 'PREMIERE_EDITION_EDITION') {
      const H6951 = PRODUCT_SERIES_LIST.find(d => d.skuId === 'H6951') || null
      if (H6951) {
        return [true, selectSeries(H6951, item.key.toLowerCase())]
      }
    }
    return [false, false]
  }

  const selectCategoryWithSource = (category: ProductCategoryConfig) =>
    selectCategory(
      category.value as ProductCategoryValue,
      ['newworks', category.value.toLowerCase()].join('$')
    )

  const selectSeriesWithSource = (data: FORMATTED_PRODUCT_SERIE) =>
    selectSeries(data, ['watch', data.skuId].join('$'))

  const simulate = async () => {
    console.log('[[ ConfAndQuery ] AnalysedData ]', {
      first: confAndQueryStore.firstClass,
      second: confAndQueryStore.secondClass,
      select: confAndQueryStore.toSelect
    })

    if (confAndQueryStore.toSelect.fromAllWorks) {
      if (confAndQueryStore.toSelect.cat) {
        selectCategory(
          confAndQueryStore.toSelect.cat as ProductCategoryValue,
          'allworks'
        )
        tryOnProduct()
      } else {
        await delay(600)
        gotoAllWorks(false, false)
      }
    } else {
      if (confAndQueryStore.firstClass.displayIndex > 0) {
        scrollFunc.value?.(confAndQueryStore.firstClass.displayIndex, false)
      }

      if (confAndQueryStore.toSelect.item) {
        if (confAndQueryStore.firstClass.index > -1) {
          const item =
            confAndQueryStore.works[confAndQueryStore.firstClass.index]

          switch (item.type) {
            case 'stackings':
              if (selectStackingSet(item)) tryOnStacking()
              break

            case 'categories':
              if (
                selectCategoryWithSource(
                  item.categories[confAndQueryStore.secondClass.index]
                )
              ) {
                tryOnProduct()
              }
              break

            case 'series':
              if (
                selectSeriesWithSource(
                  item.series[confAndQueryStore.secondClass.index]
                )
              ) {
                tryOnProduct()
              }
              break

            default:
              {
                const [isH6951, selected] = selectSeriesIfIsH6951(item)
                if (isH6951) {
                  if (selected) tryOnProduct()
                } else if (selectCategoryAndAttr(item)) tryOnProduct()
              }
              break
          }
        }
      }
    }
    isSimulated.value = true
  }

  onMounted(() => {
    nextTick().then(simulate)
  })

  return {
    isSimulated,
    scrollFunc,
    simulate,
    selectSeriesIfIsH6951,
    selectCategoryWithSource,
    selectSeriesWithSource
  }
}

export default useNewWorks
